import { useMemo, useRef } from 'react';
import { NavLink, useParams } from 'react-router';
import {
  ChevronRight,
  Download,
  ExternalLink,
  File,
  Folder,
  FolderGit2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useFilestoreLastCommits,
  useFilestoreTree,
} from '@/hooks/queries/filestore/use-filestore-tree';
import { useOrgStore } from '@/stores/org-store';
import { orgPath } from '@/utils/org-path';
import { formatBytes } from '@/utils/format-bytes';
import { filestoreFileUrl } from '@/services/filestore/file-download-url';
import { encodeFilePath } from '@/services/filestore/filestore-request';
import { forgejoTreeUrl } from '@/constants/filestore';
import { ROUTES } from '@/constants/routes';
import type { FilestoreTreeEntry } from '@/types/filestore';
import { FilestoreErrorBanner } from './components/filestore-error-banner';

const entryName = (entry: FilestoreTreeEntry): string =>
  entry.path.split('/').pop() ?? entry.path;

const formatTimestamp = (value: string | null | undefined): string =>
  value
    ? new Date(value).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';

export const FileBrowserPage = () => {
  const params = useParams();
  const repo = params.repo;
  // Splat segment: the folder path inside the repo ('' at the root).
  const dirPath = (params['*'] ?? '').replace(/\/+$/, '');

  const orgId = useOrgStore((state) => state.currentOrg?.orgid ?? null);
  const tableRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error, refetch } = useFilestoreTree(repo, dirPath);

  const entries = useMemo(() => {
    const sorted = [...(data?.entries ?? [])];
    // Directories first, then files, both alphabetical — the convention of
    // every file browser.
    sorted.sort((a, b) =>
      a.type === b.type
        ? entryName(a).localeCompare(entryName(b), undefined, {
            sensitivity: 'base',
          })
        : a.type === 'dir'
          ? -1
          : 1,
    );
    return sorted;
  }, [data?.entries]);

  const lastCommits = useFilestoreLastCommits(repo, dirPath, data?.entries);

  if (!repo) return null;

  const ref = data?.ref ?? 'main';
  const pathSegments = dirPath ? dirPath.split('/') : [];
  const repoRootLink = orgPath(
    `${ROUTES.FILES.ROOT}/${encodeURIComponent(repo)}`,
  );

  return (
    <div className='flex w-full flex-col gap-6'>
      <div className='flex items-start justify-between gap-4'>
        {/* Folder breadcrumbs: repo root, then one crumb per path segment. */}
        <nav
          aria-label='Folder path'
          className='flex min-w-0 flex-wrap items-center gap-1 text-sm'
        >
          <NavLink
            to={repoRootLink}
            className='flex items-center gap-2 font-medium hover:underline'
          >
            <FolderGit2 className='size-4 shrink-0 text-muted-foreground' />
            {repo}
          </NavLink>
          {pathSegments.map((segment, index) => {
            const isCurrent = index === pathSegments.length - 1;
            const prefix = pathSegments.slice(0, index + 1).join('/');
            return (
              <span key={prefix} className='flex items-center gap-1'>
                <ChevronRight className='size-3.5 text-muted-foreground' />
                {isCurrent ? (
                  <span className='font-medium'>{segment}</span>
                ) : (
                  <NavLink
                    to={`${repoRootLink}/${encodeFilePath(prefix)}`}
                    className='text-muted-foreground hover:underline'
                  >
                    {segment}
                  </NavLink>
                )}
              </span>
            );
          })}
        </nav>
        {orgId && (
          <Button asChild variant='outline' size='sm'>
            <a
              href={forgejoTreeUrl(orgId, repo, ref, dirPath)}
              target='_blank'
              rel='noreferrer'
            >
              Open in Forgejo
              <ExternalLink className='size-3.5' />
            </a>
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className='py-12 text-center text-sm text-muted-foreground'>
          Loading folder…
        </div>
      ) : error ? (
        <FilestoreErrorBanner message={error.message} onRetry={refetch} />
      ) : entries.length === 0 ? (
        <div className='rounded-lg border border-dashed px-6 py-10 text-center text-sm text-muted-foreground'>
          This folder is empty.
        </div>
      ) : (
        <Table tableRef={tableRef}>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className='w-28'>Size</TableHead>
              <TableHead className='w-48'>Updated</TableHead>
              <TableHead className='w-16' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.map((entry) => {
              const name = entryName(entry);
              const lastCommit = lastCommits.data?.[entry.path];
              return (
                <TableRow key={entry.path}>
                  <TableCell className='font-medium'>
                    {entry.type === 'dir' ? (
                      <NavLink
                        to={`${repoRootLink}/${encodeFilePath(entry.path)}`}
                        className='flex items-center gap-2 hover:underline'
                      >
                        <Folder className='size-4 shrink-0 text-muted-foreground' />
                        {name}
                      </NavLink>
                    ) : (
                      <a
                        href={filestoreFileUrl(
                          orgId ?? '',
                          repo,
                          entry.path,
                          ref,
                        )}
                        className='flex items-center gap-2 hover:underline'
                        title={`Download ${name}`}
                      >
                        <File className='size-4 shrink-0 text-muted-foreground' />
                        {name}
                      </a>
                    )}
                  </TableCell>
                  <TableCell className='text-muted-foreground'>
                    {entry.type === 'dir' ? '—' : formatBytes(entry.size)}
                  </TableCell>
                  <TableCell
                    className='text-muted-foreground'
                    title={lastCommit?.subject}
                  >
                    {lastCommits.isLoading ? (
                      <Skeleton className='h-4 w-32' />
                    ) : (
                      formatTimestamp(lastCommit?.timestamp)
                    )}
                  </TableCell>
                  <TableCell>
                    {entry.type === 'file' && (
                      <Button
                        asChild
                        variant='ghost'
                        size='icon'
                        aria-label={`Download ${name}`}
                      >
                        <a
                          href={filestoreFileUrl(
                            orgId ?? '',
                            repo,
                            entry.path,
                            ref,
                          )}
                          download={name}
                          title={`Download ${name}`}
                        >
                          <Download className='size-4' />
                        </a>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
